import { AddToCartFormProps } from "../components/addToCartForm/addToCartForm.models";
import { SkuDetails } from "../components/addToCartForm/sizePicker.models";
import { Product, ProductVariant } from "../services/search.service";
import { isSuitSize } from "./productSize";

function mapVariantToSkuDetails(variant: ProductVariant): SkuDetails {
    return {
        name: variant.size ?? "", // Maps the size
        skuCode: variant.custom?.EpiCode?.[0]?.label ?? "", // Safely gets SKU code
        ticket: variant.ticket ?? "", // Maps the ticket
        inventoryCount: variant.stockNumber ?? 0, // Ensures it's always a number
        regularSize: variant.size ?? "", // Important for regular sizes
        isMatrix: false, // Defaults to false unless overridden
        hasPrice: true, // Default to true (adjust if needed)
        suitSize: "", // Empty for regular sizes
        suitWidth: "",
        suitLength: "",
        matrixSizeL: "",
        matrixSizeW: "",
    };
}

export function mapProductsToAddToCartFormProps(
    productVariants: ProductVariant[],
    isMatrixSize: boolean,
    product: Product,
    store: string,
    addToCartCallback: () => void,
    itemListId?: string,
    itemListName?: string,
    showInventoryNotification?: boolean,
): AddToCartFormProps {
    let matrixSizesProp: SkuDetails[][] = [];
    let regularSizesProp: SkuDetails[][] = [];

    if (isMatrixSize) {
        const firstSize = productVariants[0]?.size ?? "";

        if (isSuitSize(firstSize)) {
            // Suit sizes -> grouped by width (matrixSizeW) and list matching lengths (matrixSizeL)
            const groupedByWidth: Record<string, SkuDetails[]> = {};

            productVariants.forEach((variant) => {
                const mappedDetails = {
                    ...mapVariantToSkuDetails(variant),
                    isMatrix: true,
                    matrixSizeL: variant.size?.slice(2) ?? "", // Extract Length (e.g., '30' from '3032')
                    matrixSizeW: variant.size?.slice(0, 2) ?? "", // Extract Width (e.g., '32' from '3032')
                    suitSize: variant.size?.slice(2) ?? "", // Suit size reuses length
                    suitWidth: variant.size?.charAt(0) ?? "", // Suit width mapped from the first digit
                    suitLength: variant.size?.charAt(1) ?? "", // Suit length mapped from second digit
                };

                if (!groupedByWidth[mappedDetails.matrixSizeW]) {
                    groupedByWidth[mappedDetails.matrixSizeW] = [];
                }
                groupedByWidth[mappedDetails.matrixSizeW].push(mappedDetails);
            });

            // Convert object values into an array of arrays
            matrixSizesProp = Object.values(groupedByWidth);
        } else {
            // Pants sizes -> each variant gets its own array with one reactive object
            matrixSizesProp = productVariants.map((variant) => {
                const size = variant.size ?? "";

                let width = "";
                let length = "";

                if (/^\d+$/.test(size.slice(0, 2))) {
                    // Case 1: Numeric width (e.g., "3032")
                    width = size.slice(0, 2);
                    length = size.slice(2);
                } else {
                    // Case 2: Letter-based width (e.g., "L30")
                    const match = size.match(/^([A-Za-z]+)(\d+)$/);
                    width = match ? match[1] : "";
                    length = match ? match[2] : "";
                }

                return [
                    {
                        ...mapVariantToSkuDetails(variant),
                        isMatrix: true,
                        matrixSizeL: length, // Correct length
                        matrixSizeW: width, // Correct width
                        suitWidth: "",
                        suitLength: "",
                        suitSize: "",
                    },
                ];
            });
        }
    } else {
        // Regular sizes -> one array containing all variants
        regularSizesProp = [productVariants.map(mapVariantToSkuDetails)];
    }

    return {
        matrixSizes: matrixSizesProp,
        regularSizes: regularSizesProp,
        shopId: store || "",
        isSizeGuide: false,
        isProductInStock: product.inStock,
        productAvailableInStore: false,
        displayName: product.title || "",
        itemColor: product.custom?.ColorName?.[0]?.label || "",
        itemMetaColor: product.custom?.MetaColor?.[0]?.label || "",
        cartStatusImage: product.imageInfo?.thumbnail || "",
        checkoutLink: "",
        esalesKey: product.key || "",
        addToCartCallback: addToCartCallback,
        isQuickAdd: true,
        itemListId: itemListId,
        itemListName: itemListName,
        categoryName: "",
        showInventoryNotification: showInventoryNotification ?? false,
    };
}
